import { useState, useMemo } from "react";
import { useQuery, getMyEdgeReports, getEdgeReportById } from "wasp/client/operations";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Sparkles, TrendingUp } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { Button } from "../client/components/ui/button";
import { Card } from "../client/components/ui/card";
import { Skeleton } from "../client/components/ui/skeleton";
import { cn } from "../client/utils";
import EdgeReportView from "./EdgeReportView";
import { edgeReportSchema } from "./reportSchema";

/**
 * "My analyses" — the retention loop. Every past report can be reopened exactly as it
 * was rendered in the drawer, together with the conversation that followed it.
 */

type QaEntry = { question: string; answer: string; askedAt: string };

const PAGE_SIZE = 12;

function scoreTone(score: number): string {
  if (score > 70) return "bg-emerald-500";
  if (score >= 50) return "bg-amber-500";
  return "bg-red-500";
}

/** Edge Score history. Pure SVG — no chart dependency for a dozen points. */
function ScoreSparkline({ scores }: { scores: number[] }) {
  const t = useTranslation();
  if (scores.length < 2) return null;

  const width = 240;
  const height = 48;
  const max = Math.max(...scores, 100);
  const min = Math.min(...scores, 0);
  const range = max - min || 1;

  const points = scores.map((score, i) => {
    const x = (i / (scores.length - 1)) * width;
    const y = height - ((score - min) / range) * height;
    return `${x},${y}`;
  });

  const last = scores[scores.length - 1];
  const first = scores[0];
  const improving = last >= first;

  return (
    <div className="flex items-center gap-3">
      <div>
        <p className="text-xs text-muted-foreground">{t.edgy.history.scoreTrend}</p>
        <p className="font-mono text-lg font-semibold">
          {first} → {last}
        </p>
      </div>
      <svg width={width} height={height} className="overflow-visible" aria-hidden>
        <polyline
          points={points.join(" ")}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={improving ? "stroke-emerald-500" : "stroke-red-500"}
        />
        {points.map((p, i) => {
          const [x, y] = p.split(",");
          return <circle key={i} cx={x} cy={y} r="2.5" className={improving ? "fill-emerald-500" : "fill-red-500"} />;
        })}
      </svg>
    </div>
  );
}

export default function EdgyReportsPage() {
  const t = useTranslation();
  const { language } = useLanguageContext();
  const locale = language === "fr" ? "fr-FR" : "en-US";

  const [take, setTake] = useState(PAGE_SIZE);
  const [strategyFilter, setStrategyFilter] = useState<string>("");
  const [openReportId, setOpenReportId] = useState<string | null>(null);

  const { data, isLoading } = useQuery(getMyEdgeReports, {
    skip: 0,
    take,
    ...(strategyFilter ? { playbookId: strategyFilter } : {}),
  });

  const { data: openReport } = useQuery(
    getEdgeReportById,
    { id: openReportId ?? "" },
    { enabled: !!openReportId }
  );

  /** Cards grouped by month, newest first — matches how traders think about periods. */
  const grouped = useMemo(() => {
    if (!data?.items) return [];
    const groups = new Map<string, typeof data.items>();

    for (const item of data.items) {
      const date = new Date(item.createdAt);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const list = groups.get(key);
      if (list) list.push(item);
      else groups.set(key, [item]);
    }

    return [...groups.entries()].map(([key, items]) => {
      const [year, month] = key.split("-");
      const label = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
      });
      return { key, label, items };
    });
  }, [data, locale]);

  const strategies = useMemo(() => {
    if (!data?.items) return [];
    const seen = new Map<string, string>();
    for (const item of data.items) {
      if (item.playbookId && !seen.has(item.playbookId)) {
        seen.set(item.playbookId, item.strategyName);
      }
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);

  // Detail view: the report exactly as the drawer rendered it, plus its conversation.
  if (openReportId) {
    const parsed = openReport?.report ? edgeReportSchema.safeParse(openReport.report) : null;
    const thread = (openReport?.qaThread as QaEntry[] | null) ?? [];

    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <Button variant="ghost" size="sm" onClick={() => setOpenReportId(null)} className="mb-4">
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.edgy.history.back}
        </Button>

        {!openReport ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : parsed?.success ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              {t.edgy.history.analysedOn.replace(
                "{date}",
                new Date(openReport.createdAt).toLocaleDateString(locale, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              )}
            </p>

            <EdgeReportView report={parsed.data} />

            {thread.length > 0 && (
              <section className="flex flex-col gap-2 border-t border-border pt-4">
                <h2 className="text-sm font-semibold">{t.edgy.history.conversation}</h2>
                {thread.map((entry, i) => (
                  <div key={i} className="rounded-lg border border-border p-3">
                    <p className="text-sm font-medium">{entry.question}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{entry.answer}</p>
                  </div>
                ))}
              </section>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t.edgy.errors.generic}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex flex-col gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">{t.edgy.history.pageTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t.edgy.history.pageSubtitle}</p>
        </div>

        {data && data.scoreHistory.length >= 2 && (
          <Card className="p-4">
            <ScoreSparkline scores={data.scoreHistory} />
          </Card>
        )}

        {strategies.length > 1 && (
          <div className="flex items-center gap-2">
            <label htmlFor="strategy-filter" className="text-xs text-muted-foreground">
              {t.edgy.history.filterStrategy}
            </label>
            <select
              id="strategy-filter"
              value={strategyFilter}
              onChange={(e) => setStrategyFilter(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            >
              <option value="">{t.edgy.history.allStrategies}</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {data && data.items.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-8 text-center">
          <Sparkles className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">{t.edgy.history.empty}</p>
          <p className="text-xs text-muted-foreground">{t.edgy.history.emptyCta}</p>
        </Card>
      )}

      <div className="flex flex-col gap-6">
        {grouped.map((group) => (
          <section key={group.key} className="flex flex-col gap-2">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {group.label}
            </h2>

            {group.items.map((item, index) => (
              <motion.button
                key={item.id}
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.3) }}
                onClick={() => setOpenReportId(item.id)}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary hover:bg-accent/40"
              >
                <span
                  className={cn(
                    "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold text-white",
                    scoreTone(item.edgeScore)
                  )}
                >
                  {item.edgeScore}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{item.strategyName}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(item.createdAt).toLocaleDateString(locale, {
                      day: "numeric",
                      month: "short",
                    })}
                    {" · "}
                    {t.edgy.history.tradesAnalysed.replace("{count}", String(item.tradesCount))}
                  </p>
                </div>

                {item.questionsCount > 0 && (
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <MessageCircle className="h-3.5 w-3.5" />
                    {item.questionsCount}
                  </span>
                )}
              </motion.button>
            ))}
          </section>
        ))}
      </div>

      {data && data.items.length < data.total && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" onClick={() => setTake((prev) => prev + PAGE_SIZE)}>
            {t.edgy.history.loadMore}
          </Button>
        </div>
      )}
    </div>
  );
}
